import { createIntl, createIntlCache } from "react-intl";
import { Platform } from "react-native";
import { constants } from "~/constants";
import { environment } from "~/environment";
import { getTranslations } from "./getTranslations";
import { polyfillIntlGetCanonicalLocales } from "./polyfillIntlGetCanonicalLocales";
import { polyfillIntlLocale } from "./polyfillIntlLocale";
import { polyfillIntlPluralRules } from "./polyfillIntlPluralRules";
import type { SupportedLanguage } from "./SupportedLanguage";
import type { IntlShape } from "react-intl";

declare global {
  const intl: IntlShape;
}

export async function initializeI18n() {
  const language = environment.LANGUAGE;

  // This will change language of the html document. This is useful for screen readers.
  if (Platform.OS === "web") {
    document.documentElement.lang = language;
  }

  // Polyfils needed for formatjs - plural rules -> https://formatjs.io/docs/polyfills/intl-pluralrules/
  await polyfillIntlGetCanonicalLocales();
  await polyfillIntlLocale();
  await polyfillIntlPluralRules(language);
  const translations = await getTranslations(language);

  const cache = createIntlCache();
  const createdIntl = createIntl(
    {
      messages: translations,
      locale: language as SupportedLanguage,
      defaultLocale: constants.DEFAULT_LANGUAGE,
    },
    cache,
  );

  // We set the `intl` variable globally. This enables us to skip calling
  // `useIntl` in every component, and using `intl` in non-react components.
  // The only reason we would want to use `useIntl` is in case the language
  // changes dynamically in runtime. However, when we change the language, we can
  // reload the app so we just set the `intl` variable globally.
  (globalThis as any).intl = createdIntl;
}
